import type { FactionId, PlayerProgress } from './types';

/**
 * Returns the XP cost to level up from currentLv → currentLv+1.
 * Formula: skillCost × (currentLv + 1)
 *   Lv1→Lv2: cost × 2
 *   Lv2→Lv3: cost × 3
 *   …
 *   Lv4→Lv5: cost × 5
 * Returns null if already at max level (5).
 */
export function levelUpCost(skillCost: number, currentLv: number): number | null {
  if (currentLv >= 5) return null;
  return skillCost * (currentLv + 1);
}

/**
 * Returns the current level of a skill for this player.
 * 0 means not unlocked.
 */
export function getSkillLevel(progress: PlayerProgress, skillId: string): number {
  return progress.skillLevels?.[skillId] ?? 0;
}

/**
 * Returns true if the player can level up the given skill:
 * - The skill must already be unlocked (level >= 1)
 * - The skill must not be at max level (< 5)
 * - The player must have enough factionXP to cover the cost
 */
export function canLevelUp(
  progress: PlayerProgress,
  skill: { id: string; cost: number; faction: FactionId },
): boolean {
  const lv = getSkillLevel(progress, skill.id);
  if (lv === 0) return false; // not unlocked
  if (lv >= 5) return false;  // already max
  const cost = levelUpCost(skill.cost, lv)!;
  return progress.factionXP[skill.faction] >= cost;
}

/**
 * Applies a level-up in place, deducting factionXP and incrementing skillLevels.
 * Returns true on success, false if canLevelUp would have returned false (no mutation).
 */
export function applyLevelUp(
  progress: PlayerProgress,
  skill: { id: string; cost: number; faction: FactionId },
): boolean {
  if (!canLevelUp(progress, skill)) return false;
  const lv = getSkillLevel(progress, skill.id);
  const cost = levelUpCost(skill.cost, lv)!;
  progress.factionXP[skill.faction] -= cost;
  progress.skillLevels[skill.id] = lv + 1;
  return true;
}

/** Maximum skill level (spec §3.1 cap). Lv0=locked, Lv1=base unlock, Lv5=max. */
export const MAX_SKILL_LEVEL = 5;

/**
 * Stat multiplier ladder per skill level (spec §3.1).
 * Index 0 = Lv1 baseline, index 4 = Lv5 cap.
 * Cap 2.10 × same-faction affinity 1.5 = 3.15 total (boss HP balance budget).
 */
export const LEVEL_DAMAGE_LADDER = [1.0, 1.2, 1.45, 1.75, 2.1] as const;

/**
 * Returns the stat multiplier for a given skill level.
 * Lv0 (locked) → 0 (caller must check before activating).
 * Lv1-5 → LEVEL_DAMAGE_LADDER[lv-1]. Lv6+ defensively clamps to Lv5 cap.
 */
export function getLevelMultiplier(lv: number): number {
  if (lv <= 0) return 0;
  if (lv >= MAX_SKILL_LEVEL) return LEVEL_DAMAGE_LADDER[MAX_SKILL_LEVEL - 1];
  return LEVEL_DAMAGE_LADDER[lv - 1];
}

/**
 * R-004 A4 — 技能升級「前→後關鍵數字對比」。
 *
 * 給定當前等級,回傳升級確認介面要顯示的關鍵戰力數字(本作以「傷害倍率」為
 * 主要量化指標 — 來自 LEVEL_DAMAGE_LADDER,是唯一參與戰鬥計算的乘數)。
 * 純函式、無副作用,供 training-hall 升級卡 + 測試共用。
 *
 * @param currentLv 當前技能等級(1..MAX_SKILL_LEVEL)。
 * @returns null 表示無法升級(未解鎖 lv<1 或已滿級);否則 from/to 倍率。
 */
export interface SkillUpgradePreview {
  fromLv: number;
  toLv: number;
  /** 傷害倍率(×N),保留一位小數方便顯示。 */
  fromDamageMult: number;
  toDamageMult: number;
}

export function getSkillUpgradePreview(currentLv: number): SkillUpgradePreview | null {
  if (currentLv < 1) return null;          // 未解鎖
  if (currentLv >= MAX_SKILL_LEVEL) return null; // 已滿級
  const toLv = currentLv + 1;
  return {
    fromLv: currentLv,
    toLv,
    fromDamageMult: getLevelMultiplier(currentLv),
    toDamageMult: getLevelMultiplier(toLv),
  };
}
